import { useMemo, useState } from 'react'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'

import BrandState from '../../components/BrandState'
import DataTableMUI from '../../components/DataTableMUI'
import { useGetFuturesReportQuery } from '../../store/api/futuresControlApi'
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { futuresAccessBaseSx, futuresAccessToggleGroupSx } from './futuresAccessUi'

type ReportKind = 'summary' | 'margin-report' | 'cash-balance' | 'position' | 'pnl' | 'check-futures' | 'reconciliation'

interface Props {
  kind: ReportKind
  embedded?: boolean
}

const API_KIND: Record<ReportKind, string> = {
  summary: 'summary',
  'margin-report': 'margin-report',
  'cash-balance': 'cash-balance',
  position: 'position',
  pnl: 'pnl',
  'check-futures': 'check-futures',
  reconciliation: 'reconciliation',
}

function columnsFromRow(row: Record<string, unknown>): GridColDef[] {
  return Object.keys(row).map((field) => ({
    field,
    headerName: field,
    flex: 1,
    minWidth: 110,
    sortable: false,
  }))
}

export default function FuturesReportView({ kind, embedded }: Props) {
  const apiKind = API_KIND[kind]
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 })
  const [checkVariant, setCheckVariant] = useState<'ld' | 'ny'>('ld')

  const { data, isLoading, isFetching, error, refetch } = useGetFuturesReportQuery({
    kind: apiKind,
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(kind === 'check-futures' ? { variant: checkVariant } : {}),
  })

  const rows = useMemo(() => {
    const items = data?.items ?? []
    return items.map((r, idx) => ({
      ...(r as Record<string, unknown>),
      id: `r-${pagination.page}-${idx}`,
    }))
  }, [data?.items, pagination.page])

  const columns = useMemo(() => {
    const first = rows[0]
    if (!first) return [] as GridColDef[]
    return columnsFromRow(first as Record<string, unknown>)
  }, [rows])

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...(embedded ? futuresAccessBaseSx : {}),
      }}
    >
      {kind === 'check-futures' ? (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={checkVariant}
          onChange={(_, v) => {
            if (v) {
              setCheckVariant(v)
              setPagination((p) => ({ ...p, page: 0 }))
            }
          }}
          sx={embedded ? futuresAccessToggleGroupSx : undefined}
        >
          <ToggleButton value="ld">LD</ToggleButton>
          <ToggleButton value="ny">NY</ToggleButton>
        </ToggleButtonGroup>
      ) : null}
      {error ? (
        <BrandState
          tone="error"
          title="Could not load data"
          description="Please refresh the page or try again."
          action={
            <Button size="small" variant="outlined" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading && rows.length === 0 ? (
        <BrandState tone="loading" title="Loading…" description="Please wait." />
      ) : rows.length === 0 ? (
        <BrandState title="No data" description="Adjust filters or check back later." />
      ) : (
        <DataTableMUI
          rows={rows}
          columns={columns}
          loading={isFetching}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          rowCount={data?.total ?? 0}
          disableMultipleRowSelection
          accessDatasheet={embedded}
          sx={embedded ? undefined : { border: 'none' }}
          height={embedded ? 'min(480px, calc(100vh - 560px))' : 480}
        />
      )}
    </Box>
  )
}
